// Renketsu mark — three-petal asterisk with the sky→cobalt gradient.
//
// Inlined as JSX so individual petals can be addressed for state-driven
// animation. Replaces the static <img src="/mark.svg" /> usage when we
// need motion. The .svg file stays as the favicon source.
//
// States (spec 16 §13):
//   idle       — still
//   breathing  — gentle 0.97↔1.03 scale, 2.4s ease-in-out infinite
//   thinking   — petals fade-pulse one-at-a-time, whole mark rotates
//                slow (10s linear); reads as "the agent is working"
//   composing  — faster rotate (4s) + brightness lift; transient
//
// `prefers-reduced-motion: reduce` collapses everything to `idle` —
// the CSS handles that, no JS branching here.

import { cn } from "@/lib/utils";

export type MarkState = "idle" | "breathing" | "thinking" | "composing";

interface MarkProps {
	state?: MarkState;
	size?: number;
	className?: string;
	/** Per-mount stable id so multiple <Mark /> on a page don't share
	 *  a gradient (which would cause visual glitches on remount). */
	gradientId?: string;
}

export function Mark({
	state = "idle",
	size = 20,
	className,
	gradientId = "mark-gradient-default",
}: MarkProps) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 32 32"
			width={size}
			height={size}
			fill="none"
			className={cn("mark", `mark-state-${state}`, className)}
			aria-hidden="true"
		>
			<defs>
				<linearGradient
					id={gradientId}
					x1="0"
					y1="0"
					x2="0"
					y2="32"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#7dd3fc" />
					<stop offset="1" stopColor="#2563eb" />
				</linearGradient>
			</defs>
			{/* Three nested layers:
			    1. OUTER <g translate(16 16)>: positions the mark at the
			       center of the viewBox. SVG attribute, never overridden.
			    2. MIDDLE <g transform="rotate(N)"> per petal: each petal's
			       60deg increment, also as an SVG attribute. Never
			       overridden, so the petals keep their angular position
			       through any CSS animation we apply below.
			    3. INNER <path .mark-petal>: the petal itself. CSS
			       animations on this path scale it along its own (already
			       rotated) Y axis to create the "shine" extrusion without
			       clobbering the petal's angular position. */}
			<g transform="translate(16 16)">
				<g
					fill={`url(#${gradientId})`}
					className="mark-petals"
				>
					<g>
						<path
							d="M 0 -12 L 2.4 0 L 0 12 L -2.4 0 Z"
							className="mark-petal mark-petal-1"
						/>
					</g>
					<g transform="rotate(60)">
						<path
							d="M 0 -12 L 2.4 0 L 0 12 L -2.4 0 Z"
							className="mark-petal mark-petal-2"
						/>
					</g>
					<g transform="rotate(120)">
						<path
							d="M 0 -12 L 2.4 0 L 0 12 L -2.4 0 Z"
							className="mark-petal mark-petal-3"
						/>
					</g>
				</g>
			</g>
		</svg>
	);
}
